import React, { FC, useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { cartMobx, mallMobx } from 'mobx/mall'
import ProductItem from '../../ProductItem'
import TopProductItem from '../../ProductItem/TopProductItem'
import { MallStyleController } from '@ctrls/mallController'
import { Empty, WaterfallSkeleton } from '@cps'
import { classNames } from '@dt/fe-utils'
import { debounce as _debounce, keyBy } from '@dt/fe-utils/dist/lodash'
import { decorationMobx, themeMobx } from '@mobxStore'
import './index.scss'

interface IProps {
  lazy?: boolean;
  products?: Array<IMallViewProduct & {
    categoryNo?: string;
    layoutKey?: string;
    layoutType?: 'top' | 'waterfall';
  }>;
  productsTop?: IMallViewProduct[];
  selectCategoryNo?: string;
  mode?: TMallDisplayMode;
  onActiveCategoryChange?: (categoryNo: string) => void;
}

// 存储计算结果
const lineToResult = {}

const WaterfallTemplate: FC<IProps> = ({ lazy = true, products: _products = [], productsTop: _productsTop = [], mode = 'classic', onActiveCategoryChange }) => {
  const [products, setProducts] = useState([])
  const [productsTop, setProductsTop] = useState([])
  const isClassicBig = mode === 'classicBig'
  const [productStyleMap, setProductStyleMap] = useState({} as {
    [no: string]: {
      left: number;
      top: number;
    };
  })
  const [height, setHeight] = useState(420)
  const hashProduct = useMemo(() => {
    const layoutVersion = isClassicBig ? 'fixed-image-v1:' : ''
    return mode + layoutVersion + products.map(p => `${p.layoutKey || p.no}:${p.layoutType || ''}`).join('')
  }, [products, mode])
  const categoryFirstProductMap = useMemo(() => {
    return products.reduce((result, product) => {
      if (product.categoryNo && !result[product.categoryNo]) {
        result[product.categoryNo] = product.layoutKey || product.no
      }
      return result
    }, {} as { [categoryNo: string]: string })
  }, [products])
  // 设置商品置顶
  const calcWaterfall = () => {
    const payload = MallStyleController.waterfall(products, lazy, isClassicBig ? 'layoutKey' : 'no', undefined, mode)
    if (payload) {
      const { styleMap, height } = payload
      lineToResult[hashProduct] = payload
      setProductStyleMap(styleMap)
      setHeight(height)
    } else {
      setTimeout(() => {
        calcWaterfall()
      }, 300)
    }
  }
  const handleClassicBigScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isClassicBig) return
    const scrollContainer = e.currentTarget
    const scrollTop = scrollContainer.scrollTop
    MallStyleController.scroll(scrollTop)
    const anchors = [...scrollContainer.querySelectorAll('.mall-category-anchor')] as HTMLElement[]
    if (!anchors.length) return
    if (scrollTop <= 8) {
      onActiveCategoryChange?.(anchors[0].dataset.categoryNo || '')
      return
    }
    if (scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1) {
      onActiveCategoryChange?.(anchors[anchors.length - 1].dataset.categoryNo || '')
      return
    }
    const containerTop = scrollContainer.getBoundingClientRect().top
    const activeAnchor = anchors.reduce((active, anchor) => {
      const anchorTop = anchor.getBoundingClientRect().top - containerTop + scrollTop
      return anchorTop <= scrollTop + 8 ? anchor : active
    }, null as HTMLElement)
    onActiveCategoryChange?.(activeAnchor?.dataset.categoryNo || '')
  }
  useEffect(() => {
    if (products.length && hashProduct) {
      if (lineToResult[hashProduct]) {
        setProductStyleMap(lineToResult[hashProduct].styleMap)
        setHeight(lineToResult[hashProduct].height)
      } else {
        setTimeout(() => {
          calcWaterfall()
        }, 50)
      }
    }
  }, [hashProduct])
  useEffect(() => {
    setProductsTop(MallStyleController.setProductImgHeight(_productsTop, 1, 0, mode))
    setProducts(MallStyleController.setProductImgHeight(_products, 2, 2, mode))
    window.addEventListener(window.onorientationchange ? 'orientationchange' : 'resize', () => {
      calcWaterfall()
    }, false)
  }, [_products, _productsTop, isClassicBig])
  useEffect(() => {
    window.addEventListener(window.onorientationchange ? 'orientationchange' : 'resize', () => {
      calcWaterfall()
    }, false)
  }, [])
  return (
    <>
      {
        mallMobx.isLoading ? <WaterfallSkeleton /> : mallMobx.products.length ? ( // 且有餐品
          <div className={classNames('mall-product-list-content', { 'classic-big': isClassicBig })} onScroll={handleClassicBigScroll}>
            {/* 置顶商品 */}
            <div className='mall-product-top-list-wrapper'>
              {
                productsTop.map((product) => {
                  const count = cartMobx.cartResult?.productNoToCntMap?.[product.no] || 0
                  return (<TopProductItem decorate={decorationMobx.mallGlobalDecorates || {} as IMallGlobalDecorates} key={product.no} product={product} count={count} backgroundMode={themeMobx.data.backgroundMode} />)
                })
              }
            </div>
            <div className='mall-waterfall-template' style={{ height: products.length ? height : 0 }}>
              {
                Object.keys(categoryFirstProductMap).map((categoryNo) => {
                  const layoutKey = categoryFirstProductMap[categoryNo]
                  const style = productStyleMap[layoutKey]
                  return style
                    ? <div className='mall-category-anchor' id={`mall-category-item-${categoryNo}`} data-category-no={categoryNo} style={{ top: style.top }} key={categoryNo} />
                    : null
                })
              }
              {
                products.map((product) => {
                  const count = cartMobx.cartResult?.productNoToCntMap?.[product.no] || 0
                  const layoutKey = product.layoutKey || product.no
                  const style = productStyleMap[layoutKey] || { visibility: 'hidden' }
                  return <ProductItem decorate={decorationMobx.mallGlobalDecorates || {} as IMallGlobalDecorates} style={style} layoutKey={layoutKey} key={layoutKey} product={product} count={count} lazy={lazy} backgroundMode={themeMobx.data.backgroundMode} imgLoaded={mallMobx.imgLoadingMap[product.no]} fixedImageHeight={isClassicBig} />
                })
              }
            </div>
            {mallMobx.products.length >= 4 && <div className='product-list-deep'>{i18n.t('mall.deep')}</div>}
            {themeMobx.brandTemplateDetails?.globalDecorates?.tabBarSetting?.pagesShow && themeMobx.brandTemplateDetails?.globalDecorates?.tabBarSetting?.pagesShow?.['mall'] === 1 && <div className='product-list-deep-tab-fill'></div>}
          </div>
        ) : ( // 无商品
          <Empty msg={i18n.t('mall.noProducts')} height='55vh' />
        )
      }
    </>
  )
}
export default observer(WaterfallTemplate)
